// The ONE layout of a repeated block's copies, on every surface: the in-app
// runtime (Run view / preview / site viewer), the downloaded runtime (Python
// and J2EE serve the same client build), the static site prerender and the
// designer canvas. Each surface still expands its own copies (it owns its
// contexts and its block host) and hands them here through `renderCopy`; how
// they are ARRANGED — a stack, a grid, lanes on a board, positions on a plane —
// selected, and highlighted when new is decided once.
//
//   flow        the original flat stack
//   grid        the responsive grid `card_grid` uses
//   board       lanes by a column's value (fixed `lanes`, or one per distinct
//               value in row order); optional lane header blocks; drag a card
//               to another lane to run `dropAction`
//   positioned  each copy at its row's own x / y / w / h on a design plane
//               that scales with the container (a floor plan, a bin map)
//
// Every row handed to `renderCopy` is DECORATED with `_key`, `_selected` and
// `_new` ('true' / 'false'), so a style rule or a visibility condition inside
// the copy can read them like any column.

import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import type { BlockRepeat, PageDataSource, RepeatLane } from '@twasta/page-schema'
import { repeatLayoutStyle } from '../renderers'
import { provideRepeatCell } from '../ThemedPageShell'
import { tw } from '../testHooks'
import { RESPONSIVE_BREAKPOINTS } from '../theme'
import { ciGet, parseIdList, pruneSelection, rowKey, toggleSelection } from './ids'

export type Row = Record<string, unknown>

// ── New-row highlight, published by a surface's live-source hook ────
export interface LiveRowsState {
  /** Keys that arrived on the latest read, per data-source id. */
  newKeys: Record<string, Set<string>>
  /** How long a new copy pulses, per source (seconds; 0 = off). */
  highlightSeconds: Record<string, number>
}
export const LiveRowsContext = createContext<LiveRowsState | null>(null)

export interface RepeatSelection {
  value: unknown
  set: (next: string) => void
}

export interface LaneInfo {
  value: string
  label: string
  color?: string
  rows: Row[]
  /** `{{lane.*}}` — value/label/count/ids plus every column of its first row. */
  vars: Row
}

export interface RepeatFrameProps {
  blockId: string
  repeat: BlockRepeat
  source?: PageDataSource
  /** Rows to show — already capped by `max` / pagination by the caller. */
  rows: Row[]
  /** Render one copy for a decorated row. `copyId` is the copy's block id. */
  renderCopy: (row: Row, index: number, copyId: string) => React.ReactNode
  /** Draw a board lane's header blocks (absent ⇒ the default caption). */
  renderLaneHeader?: (lane: LaneInfo) => React.ReactNode
  /** The selection page variable (absent ⇒ copies are not selectable). */
  selection?: RepeatSelection
  /** True when the template carries its own `on_click`: then a tap is the
   *  block's, and selection is only by its checkbox-like ring. */
  templateHasClick?: boolean
  /** A card dropped on another lane (board `dropAction`). */
  onDropToLane?: (row: Row, laneValue: string) => void
  /** False on the static site and the canvas: no handlers, no pruning. */
  interactive?: boolean
  /** Copy id convention of the surface (`${id}__${i}` by default). */
  copyIdOf?: (index: number) => string
}

const GRID_LIKE = new Set(['grid', 'board', 'positioned'])

/** Lanes for a board: the fixed list (in its order, unknown values dropped
 *  into a trailing "Other" lane) or one per distinct value in row order. */
export function boardLanes(rows: Row[], groupBy: string | undefined, fixed?: RepeatLane[]): LaneInfo[] {
  const valueOf = (r: Row) => {
    const v = ciGet(r, groupBy)
    return v == null ? '' : String(v)
  }
  const order: { value: string; label: string; color?: string }[] = []
  const seen = new Set<string>()
  if (Array.isArray(fixed) && fixed.length) {
    for (const l of fixed) {
      const v = String(l?.value ?? '')
      if (seen.has(v)) continue
      seen.add(v)
      order.push({ value: v, label: l.label || v, color: l.color })
    }
  }
  const dynamic = !(Array.isArray(fixed) && fixed.length)
  const byValue = new Map<string, Row[]>()
  const other: Row[] = []
  for (const r of rows) {
    const v = valueOf(r)
    if (!seen.has(v)) {
      if (dynamic) {
        seen.add(v)
        order.push({ value: v, label: v || '—' })
      } else {
        other.push(r)
        continue
      }
    }
    const list = byValue.get(v)
    if (list) list.push(r)
    else byValue.set(v, [r])
  }
  const lanes = order.map((l) => laneOf(l.value, l.label, l.color, byValue.get(l.value) || []))
  if (other.length) lanes.push(laneOf('__other__', 'Other', undefined, other))
  return lanes
}

function laneOf(value: string, label: string, color: string | undefined, rows: Row[]): LaneInfo {
  const first = rows[0] || {}
  const ids = rows.map((r) => (r._key as string) || rowKey(r)).filter(Boolean)
  return {
    value, label, color, rows,
    vars: {
      ...first,
      value, label, count: String(rows.length),
      ids: ids.some((k) => k.includes(',') || k.startsWith('{')) ? JSON.stringify(ids) : ids.join(','),
    },
  }
}

/** Geometry of one copy on a positioned plane, in percent of the plane. */
export function planeBox(row: Row, layout: BlockRepeat['layout']): { left: number; top: number; width: number; height: number } {
  const cw = Math.max(1, Number(layout?.canvasWidth) || 1000)
  const ch = Math.max(1, Number(layout?.canvasHeight) || 600)
  const num = (spec: string | undefined, fallback: number) => {
    if (spec == null || spec === '') return fallback
    const direct = Number(spec)
    if (Number.isFinite(direct)) return direct
    const n = Number(ciGet(row, spec))
    return Number.isFinite(n) ? n : fallback
  }
  const x = num(layout?.x, 0)
  const y = num(layout?.y, 0)
  const w = num(layout?.w, 100)
  const h = num(layout?.h, 80)
  const pct = (v: number, of: number) => Math.round((v / of) * 10000) / 100
  return { left: pct(x, cw), top: pct(y, ch), width: pct(w, cw), height: pct(h, ch) }
}

const FRAME_CSS = `
@keyframes pg-live-new{0%{box-shadow:0 0 0 4px var(--pg-primary,#2563eb)}70%{box-shadow:0 0 0 4px var(--pg-primary,#2563eb)}100%{box-shadow:0 0 0 0 transparent}}
[data-pg-copy][data-pg-selectable]{cursor:pointer}
[data-pg-copy][data-pg-selected="true"]{outline:3px solid var(--pg-primary,#2563eb);outline-offset:2px;border-radius:var(--pg-radius,8px)}
[data-pg-copy]:focus-visible{outline:3px solid var(--pg-primary,#2563eb);outline-offset:2px}
[data-pg-lane][data-pg-drop-over]{background:color-mix(in srgb,var(--pg-primary,#2563eb) 8%,transparent)}
@media (prefers-reduced-motion: reduce){[data-pg-copy][data-pg-new="true"]{animation:none!important}}
`

export function RepeatFrame({
  blockId, repeat, source, rows, renderCopy, renderLaneHeader, selection, templateHasClick,
  onDropToLane, interactive = true, copyIdOf,
}: RepeatFrameProps): React.ReactElement {
  const live = useContext(LiveRowsContext)
  const layout = repeat.layout
  const mode = layout?.mode || 'flow'
  const keyColumn = repeat.select?.key || source?.refresh?.keyColumn
  const sourceId = source?.id || repeat.source
  const newSet = live?.newKeys?.[sourceId]
  const highlight = live?.highlightSeconds?.[sourceId] ?? 0
  const selected = useMemo(() => new Set(parseIdList(selection?.value)), [selection?.value])
  const idOf = copyIdOf || ((i: number) => `${blockId}__${i}`)

  const decorated = useMemo<Row[]>(() => rows.map((r) => {
    const key = rowKey(r, keyColumn)
    return {
      ...r,
      _key: key,
      _selected: selected.has(key) ? 'true' : 'false',
      _new: newSet && newSet.has(key) ? 'true' : 'false',
    }
  }), [rows, keyColumn, selected, newSet])

  // A selected key whose row has gone (the item was finished and left the
  // source) drops out of the selection, so a bulk action never re-acts on it.
  const setSel = selection?.set
  const selValue = selection?.value
  useEffect(() => {
    if (!interactive || !setSel || !repeat.select) return
    const present = new Set(decorated.map((r) => String(r._key)))
    const next = pruneSelection(selValue, present)
    if (next !== (selValue == null ? '' : String(selValue))) setSel(next)
  }, [interactive, setSel, selValue, decorated, repeat.select])

  const dragRow = useRef<Row | null>(null)
  const selectable = !!(repeat.select && selection && interactive)
  const gridLike = GRID_LIKE.has(mode)

  // A repeat nothing interacts with renders exactly the DOM it always has
  // (each copy's own block is the stack / grid item), so a published page
  // that predates these features is byte-identical. Only a repeat that is
  // selectable, live, a board or a plane wraps each copy — consistently, so
  // a copy never remounts when it starts or stops pulsing.
  const wrap = selectable || !!source?.refresh || mode === 'board' || mode === 'positioned'

  const copy = (row: Row, i: number, extraStyle?: React.CSSProperties) => {
    const copyId = idOf(i)
    if (!wrap) {
      return <React.Fragment key={copyId}>{provideRepeatCell(gridLike, copyId, renderCopy(row, i, copyId))}</React.Fragment>
    }
    const key = String(row._key || '')
    const isSel = row._selected === 'true'
    const isNew = row._new === 'true'
    const toggle = () => {
      if (!selection || !repeat.select) return
      selection.set(toggleSelection(selection.value, key, repeat.select.mode === 'single' ? 'single' : 'multi'))
    }
    const canDrag = interactive && mode === 'board' && !!onDropToLane && !!layout?.dropAction
    const props: React.HTMLAttributes<HTMLDivElement> & Record<string, unknown> = {
      'data-pg-copy': key || String(i),
      'data-pg-selected': selectable ? String(isSel) : undefined,
      'data-pg-selectable': selectable ? '' : undefined,
      'data-pg-new': isNew ? 'true' : undefined,
      ...tw('row', key ? `${blockId}:${key}` : undefined, { selected: selectable && isSel }),
      // A copy nothing interacts with stays invisible to layout (exactly the
      // DOM a repeat has always produced: the copy's own block is the grid
      // item). One that is selectable, pulsing, draggable or placed becomes
      // the item itself so it can carry the ring / animation / position.
      style: (!selectable && !canDrag && !extraStyle && mode !== 'board' && !(isNew && highlight > 0))
        ? { display: 'contents' }
        : {
            position: extraStyle ? 'absolute' : 'relative',
            ...(gridLike && mode !== 'board' ? { height: '100%' } : null),
            ...(isNew && highlight > 0 ? { animation: `pg-live-new ${highlight}s ease-out 1` } : null),
            ...extraStyle,
          },
    }
    if (selectable && !templateHasClick) {
      props.role = 'button'
      props.tabIndex = 0
      props['aria-pressed'] = isSel
      props.onClick = (e: React.MouseEvent) => {
        // A real control inside the copy (a button, a link, a field) keeps its click.
        if ((e.target as HTMLElement)?.closest?.('button,a,input,select,textarea,[role="button"]:not([data-pg-copy])')) return
        toggle()
      }
      props.onKeyDown = (e: React.KeyboardEvent) => {
        if (e.target !== e.currentTarget) return
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle() }
      }
    }
    if (canDrag) {
      props.draggable = true
      props.onDragStart = (e: React.DragEvent) => {
        dragRow.current = row
        try { e.dataTransfer.setData('text/plain', key); e.dataTransfer.effectAllowed = 'move' } catch { /* old browsers */ }
      }
      props.onDragEnd = () => { dragRow.current = null }
    }
    return (
      <div key={copyId} {...props}>
        {provideRepeatCell(gridLike, copyId, renderCopy(row, i, copyId))}
      </div>
    )
  }

  const css = <style>{FRAME_CSS}</style>

  if (mode === 'board') {
    const lanes = boardLanes(decorated, layout?.groupBy, layout?.lanes)
    const laneWidth = Math.max(160, Number(layout?.laneWidth) || 280)
    const gap = layout?.gap ?? 16
    let index = 0
    return (
      <div data-pg-block={blockId} data-pg-board="" style={{ display: 'flex', gap, overflowX: 'auto', alignItems: 'flex-start', paddingBottom: 4 }}>
        {css}
        {lanes.map((lane) => {
          const laneProps: Record<string, unknown> = {}
          if (interactive && onDropToLane && layout?.dropAction && lane.value !== '__other__') {
            laneProps.onDragOver = (e: React.DragEvent) => {
              e.preventDefault()
              ;(e.currentTarget as HTMLElement).setAttribute('data-pg-drop-over', '')
            }
            laneProps.onDragLeave = (e: React.DragEvent) => { (e.currentTarget as HTMLElement).removeAttribute('data-pg-drop-over') }
            laneProps.onDrop = (e: React.DragEvent) => {
              e.preventDefault()
              ;(e.currentTarget as HTMLElement).removeAttribute('data-pg-drop-over')
              const row = dragRow.current
              dragRow.current = null
              if (!row) return
              const from = String(ciGet(row, layout?.groupBy) ?? '')
              if (from !== lane.value) onDropToLane(row, lane.value)
            }
          }
          return (
            <section
              key={lane.value}
              data-pg-lane={lane.value}
              {...tw('group', `${blockId}:${lane.value}`)}
              {...laneProps}
              style={{
                flex: `0 0 ${laneWidth}px`, display: 'flex', flexDirection: 'column', gap: Math.min(gap, 12),
                background: 'var(--pg-surface-alt, rgba(127,127,127,0.06))', borderRadius: 'var(--pg-radius, 10px)',
                padding: 10, borderTop: `4px solid ${lane.color || 'var(--pg-primary, #2563eb)'}`, minHeight: 80,
              }}
            >
              {renderLaneHeader
                ? renderLaneHeader(lane)
                : (
                  <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 600 }}>
                    <span>{lane.label}</span>
                    <span style={{ fontSize: 12, opacity: 0.7, padding: '1px 8px', borderRadius: 999, background: 'rgba(127,127,127,0.15)' }}>{lane.rows.length}</span>
                  </header>
                )}
              {lane.rows.map((r) => copy(r, index++))}
            </section>
          )
        })}
      </div>
    )
  }

  if (mode === 'positioned') {
    const cw = Math.max(1, Number(layout?.canvasWidth) || 1000)
    const ch = Math.max(1, Number(layout?.canvasHeight) || 600)
    const cls = `pg-plane-${blockId.replace(/[^a-zA-Z0-9_-]/g, '_')}`
    const toGrid = layout?.mobile === 'grid'
    const gridCss = toGrid
      ? `@media (max-width:${RESPONSIVE_BREAKPOINTS.tablet}px){.${cls}{aspect-ratio:auto!important;display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:12px}.${cls}>[data-pg-copy]{position:relative!important;left:auto!important;top:auto!important;width:auto!important;height:auto!important;min-height:80px}}`
      : ''
    return (
      <div
        data-pg-block={blockId}
        data-pg-plane=""
        className={cls}
        style={{ position: 'relative', width: '100%', aspectRatio: `${cw} / ${ch}` }}
      >
        {css}
        {gridCss ? <style>{gridCss}</style> : null}
        {decorated.map((r, i) => {
          const b = planeBox(r, layout)
          return copy(r, i, { left: `${b.left}%`, top: `${b.top}%`, width: `${b.width}%`, height: `${b.height}%` })
        })}
      </div>
    )
  }

  const gridStyle = repeatLayoutStyle(layout)
  return (
    <div data-pg-block={blockId} style={gridStyle || { display: 'contents' }}>
      {wrap ? css : null}
      {decorated.map((r, i) => copy(r, i))}
    </div>
  )
}
